import { styled } from '@mui/material/styles';
import { SelectableChip } from '@components/Chip';
import GoogleMap from '@components/GoogleMap';
import Overline from '@components/Overline';
import { Typography } from '@components/Typography';
import { useTranslation } from '@hooks';
import { alertActions } from '@actions';
import { formatNumber } from '@helpers/formatNumbers';

export const EMPTY = '—';

export const showRescanError = (dispatch, t, error) => {
    if (error?.status === 409) {
        dispatch(alertActions.error(t('AnalyticsVisibility.rescanConflict')));
    } else if (!error?._serverError) {
        dispatch(alertActions.error(t('apiErrors.something_wrong')));
    }
};

export const formatReviews = (votes, velocityPerMonth) => {
    if (votes == null) return EMPTY;
    if (velocityPerMonth == null) return formatNumber(votes);
    const sign = velocityPerMonth > 0 ? '+' : '';
    return `${formatNumber(votes)} (${sign}${formatNumber(velocityPerMonth)})`;
};

export const RANK_COLORS = {
    top3: 'var(--success-main)',
    fourToTen: '#FFC107',
    elevenToTwenty: '#FF9800',
    notInTop20: '#9E9E9E',
};

const RANK_BADGE_STYLES = {
    top3: { backgroundColor: 'var(--secondary-light)', color: 'var(--success-main)' },
    fourToTen: { backgroundColor: '#FFF3E0', color: '#E65100' },
    elevenToTwenty: { backgroundColor: '#FDECEA', color: 'var(--error-main)' },
    notInTop20: { backgroundColor: 'var(--action-hover)', color: 'var(--text-secondary)' },
};

export const getRankBucket = rank => {
    if (rank === null || rank === undefined || rank > 20) return 'notInTop20';
    if (rank <= 3) return 'top3';
    if (rank <= 10) return 'fourToTen';
    return 'elevenToTwenty';
};

export const getRankColor = rank => RANK_COLORS[getRankBucket(rank)];

export const buildMarkers = keyword =>
    (keyword?.grid || []).map(point => ({
        lat: point.latitude,
        lng: point.longitude,
        label: point.rank != null ? String(point.rank) : '20+',
        color: getRankColor(point.rank),
        row: point.row,
        col: point.col,
    }));

const describeGrid = (grid, gridMeta) => {
    if (gridMeta?.size) {
        return { size: gridMeta.size, km: gridMeta.km ?? null };
    }
    if (!grid?.length) return null;
    const size = Math.max(...grid.map(cell => Math.max(cell.row, cell.col))) + 1;
    const adjacent = grid
        .map(cell => [cell, grid.find(other => other.col === cell.col && other.row === cell.row + 1)])
        .find(([, below]) => below);
    if (!adjacent) return { size, km: null };
    const km = Math.abs(adjacent[1].latitude - adjacent[0].latitude) * 111.32;
    return { size, km: Math.round(km * 10) / 10 };
};

export const StatTiles = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 8,
});

const StatTile = styled('div')({
    backgroundColor: 'var(--action-hover)',
    borderRadius: 8,
    padding: '14px 6px',
    textAlign: 'center',
});

const StatValue = styled('span')({
    fontFamily: 'Rubik, sans-serif',
    fontSize: '1.25rem',
    fontWeight: 500,
    lineHeight: '28px',
    color: 'var(--primary-main)',
    display: 'block',
    whiteSpace: 'nowrap',
});

export const SectionHeading = styled(Overline)({
    margin: '24px 0 8px',
});

export const ChipsWrapper = styled('div')({
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
});

const KeywordLabel = styled('span')({
    display: 'flex',
    alignItems: 'center',
    gap: 6,
});

export const RankBadge = styled('span', { shouldForwardProp: prop => prop !== 'bucket' })(({ bucket }) => ({
    padding: '2px 6px',
    borderRadius: 10,
    fontWeight: 500,
    ...RANK_BADGE_STYLES[bucket],
}));

const Legend = styled('div')({
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
    marginTop: 12,
});

const LegendItem = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 6,
});

const LegendDot = styled('span')({
    width: 12,
    height: 12,
    borderRadius: '50%',
    display: 'inline-block',
});

export const Stat = ({ value, label }) => (
    <StatTile>
        <StatValue>{value}</StatValue>
        <Typography variant="caption" color="textSecondary">
            {label}
        </Typography>
    </StatTile>
);

export const KeywordChips = ({ keywords, selectedKeywordId, onSelect }) => (
    <ChipsWrapper>
        {keywords.map(keyword => (
            <SelectableChip
                key={keyword.keywordId}
                label={
                    <KeywordLabel>
                        {keyword.text}
                        <RankBadge bucket={getRankBucket(keyword.avgRank)}>
                            {keyword.avgRank != null ? keyword.avgRank.toFixed(1) : EMPTY}
                        </RankBadge>
                    </KeywordLabel>
                }
                selected={keyword.keywordId === selectedKeywordId}
                onClick={() => onSelect(keyword.keywordId)}
            />
        ))}
    </ChipsWrapper>
);

export const GridMap = ({ keyword, mapId, markers, center, gridMeta = null, onMarkerClick = null }) => {
    const { t } = useTranslation();

    const heading = () => {
        const grid = describeGrid(keyword?.grid, gridMeta);
        const parts = [t('AnalyticsVisibility.mapRankTitle', { keyword: `"${keyword?.text || ''}"` })];
        if (grid) {
            parts.push(`${grid.size}×${grid.size}`);
            if (grid.km) {
                parts.push(
                    t('AnalyticsVisibility.gridSpacing', {
                        km: grid.km % 1 === 0 ? grid.km.toFixed(0) : grid.km.toFixed(1),
                    }),
                );
            }
        }
        return parts.join(' · ');
    };

    return (
        <>
            <SectionHeading>{heading()}</SectionHeading>
            {center ? (
                <>
                    <GoogleMap
                        id={mapId}
                        options={{ center, zoom: 12 }}
                        markers={markers}
                        onMarkerClick={onMarkerClick}
                        style={{ height: 320, width: '100%' }}
                    />
                    <Legend>
                        {['top3', 'fourToTen', 'elevenToTwenty', 'notInTop20'].map(bucket => (
                            <LegendItem key={bucket}>
                                <LegendDot sx={{ backgroundColor: RANK_COLORS[bucket] }} />
                                <Typography variant="caption">{t(`AnalyticsVisibility.${bucket}`)}</Typography>
                            </LegendItem>
                        ))}
                    </Legend>
                </>
            ) : (
                <Typography variant="body2" color="textSecondary" sx={{ display: 'block' }}>
                    {t('AnalyticsVisibility.noCoordinates')}
                </Typography>
            )}
        </>
    );
};
